import { useState } from 'react'

import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'

import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Item, getIdentifierEnumFromString } from 'modules/processes/domain'
import { useUpsertActionItem } from 'modules/processes/hooks/actions/useUpsertActionItem'
import { useProcessContext } from 'modules/processes/pages/Process/ProcessRootPage'

import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import { useItemProvider } from 'modules/processes/components/ItemWizard/Providers/item'
import { useActionWizard } from './ActionProvider'
import { actionSelectorStep } from './Steps/ActionSelector'
import { fillInformationStep } from './Steps/FillInformation'
import { previewInformationStep } from './Steps/Preview'
import { WizardData } from './types'

type Props = {
  onClose: () => void
  item?: Item
}

const ActionWizard = ({ onClose, item }: Props) => {
  const { loading, isActionValid, fileIds } = useActionWizard()
  const { form } = useFormProvider()
  const { process } = useProcessContext()
  const { upsertActionItem } = useUpsertActionItem()
  const { itemType: configurationIdentifier } = useItemProvider()

  const steps: WizardStep<WizardData>[] = useMemo(() => {
    return [actionSelectorStep(), fillInformationStep(), previewInformationStep()]
  }, [])

  const [title, setTitle] = useState<string>()

  const onSubmit = async () => {
    const {
      configuration,
      attachments,
      configurationId,
      assignee_strategy,
      assignee_ids,
      position,
      step_id,
      title: itemTitle,
    } = form.values
    const itemId = item?.id

    const commonData = {
      configuration: JSON.stringify(configuration),
      configurationIdentifier,
      title: itemTitle || '',
      item: {
        stepId: step_id,
        position: +position,
        assigneeStrategy: assignee_strategy,
        assigneeIds: assignee_ids,
      },
      ...(attachments && { filesToAdd: attachments }),
    }

    const success = await upsertActionItem(commonData, process.id, configurationId, itemId, fileIds)

    if (!success) return
    app.setFlash({
      type: 'correct',
      message: i18n.t('processes.process_settings.actions.success'),
    })
    onClose()
  }

  const currentIdentifier = getIdentifierEnumFromString(item?.configurationIdentifier)

  const currentWizardAction = currentIdentifier && {
    identifier: currentIdentifier,
    title: '',
    includeAssignees: false,
    includeFiles: false,
  }

  return (
    <Wizard.Modal
      locked={loading}
      icon="monitor-flash"
      title={title}
      steps={steps}
      initialData={{
        action: currentWizardAction,
        setTitle,
        isValid: isActionValid,
      }}
      onSubmit={onSubmit}
      size="large"
      onClose={onClose}
      bottomSpacing={false}
      {...(item && { defaultStepIndex: 1 })}
    />
  )
}

export default ActionWizard
